import { State } from '../state'
import type { BookingflowStateMachine } from '../../bookingflowstatemachine'
import {
    detailsActions,
    detActions,
    DetailsStateActions,
    IGetData,
    DetailsStateDependencies,
} from './detailsState.types'
import CheckoutState from '../checkoutState'
import type { CheckoutStateDependencies } from '../checkoutState/checkoutState.types'
import type { BasisState } from '../..'
import {
    Product,
    KlippeCard,
    CompanyLead,
    Address,
    BookingReasons,
    GiftCard,
    CustomerType,
    Booking,
    Service,
    BasisType,
    VoucherOrNone,
    Customer,
    Company,
    PrivateCustomer,
    GoDreamGiftCard,
    ThirdPartyGiftCard,
} from '@raskrask-dk/raskrask-utils'

const DETAILS_STATE = 'DETAILS'
export default class DetailsState extends State<
    DetailsStateActions,
    typeof DETAILS_STATE
> {
    public readonly type = DETAILS_STATE
    private _product: Product | CompanyLead
    private _address: Address
    private _specialNeeds: boolean | undefined
    private _problemDescription: string | undefined
    private _acceptConditions: boolean | undefined
    private _customerName: string | undefined
    private _customerPhone: number | undefined
    private _customerEmail: string | undefined
    private _promoCode: string | undefined
    private _klippeCardCode: string | undefined
    private _bookingReasons: BookingReasons = []
    private _parkingText: string | undefined
    private _companyAdminName: string | undefined
    private _companyName: string | undefined
    private _giftCard: GiftCard | undefined
    private _customerType: CustomerType

    /**
     *
     * @param bookingflowStateMachine is required always
     * @param product is required for entering the detailsstate
     */
    constructor(
        bookingflowStateMachine: BookingflowStateMachine,
        dependencies: DetailsStateDependencies
    ) {
        super(bookingflowStateMachine)
        const {
            product,
            address,
            customerType,
            customerName,
            customerEmail,
            customerPhone,
            companyAdminName,
            companyName,
            klippeCardCode,
        } = dependencies
        this._product = product
        this._address = address
        this._customerType = customerType
        this._klippeCardCode = klippeCardCode
        this._customerName = customerName
        this._customerEmail = customerEmail
        this._customerPhone = customerPhone
        this._companyAdminName = companyAdminName
        this._companyName = companyName
    }

    private nextState(): CheckoutState | DetailsState {
        const deps = this.checkoutDependencies
        return new CheckoutState(this.bookingflowStateMachine, deps)
    }

    private get checkoutDependencies(): CheckoutStateDependencies {
        const product = this._product as Product
        if (product instanceof Booking) {
            const problemDescription = this.problemDescription ?? ''
            const parkingText = this.parkingText
                ? `Parkering: ${this.parkingText}`
                : ''
            product.problemDescription = `
      ${problemDescription} 
      ${parkingText}
      `
        }

        const customer = this.customer
        const address = this._address
        const promoCode = this._promoCode
        const voucher = this.voucher
        return {
            customer: customer,
            product: product,
            address: address,
            promoCode: promoCode,
            voucher: voucher,
        }
    }

    doAction(action: DetailsStateActions): DetailsState | CheckoutState {
        switch (action.type) {
            case 'NEXT_STATE':
                return action.isValid() ? this.nextState() : this
            case 'ACCEPT_CONDITIONS':
                this._acceptConditions = action.data
                return this
            case 'SET_PROBLEM_DESCRIPTION':
                this._problemDescription = action.data
                ;(this._product as Booking).problemDescription = action.data
                return this
            case 'SET_SPECIALNEEDS':
                this._specialNeeds = action.data
                return this
            case 'SET_CUSTOMER_EMAIL':
                this._customerEmail = action.data
                return this
            case 'SET_CUSTOMER_NAME':
                this._customerName = action.data
                return this
            case 'SET_CUSTOMER_PHONE':
                this._customerPhone = action.data
                return this
            case 'SET_GIFT_CARD':
                this._giftCard = action.data
                return this
            case 'SET_KLIPPE_CARD':
                this._klippeCardCode = action.data
                return this
            case 'SET_PROMO_CODE':
                this._promoCode = action.data
                return this
            case 'SET_BOOKING_REASONS':
                this._bookingReasons = action.data
                return this
            case 'SET_PARKING_TEXT':
                this._parkingText = action.data
                return this
            case 'SET_COMPANY_ADMIN_NAME':
                this._companyAdminName = action.data
                return this
            case 'SET_COMPANY_NAME':
                this._companyName = action.data
                return this
            default:
                throw new Error(
                    'These are not the droids you are looking for...'
                )
        }
    }

    /**
     *
     * @param action - The action that is given
     */
    public unset = (_action: DetailsStateActions): void => {
        return
    }

    public getActions = (): detActions[] =>
        detailsActions.filter((action) => new action(this).isDoable())

    public getData(): IGetData {
        return {
            address: this.address,
            product: this._product,
            specialNeeds: this.specialNeeds,
            problemDescription: this.problemDescription,
            acceptConditions: this.acceptConditions,
            customerName: this.customerName,
            customerPhone: this.customerPhone,
            customerEmail: this.customerEmail,
            promoCode: this.promoCode,
            klippeCardCode: this.klippeCardCode,
            bookingReasons: this.bookingReasons,
            parkingText: this.parkingText,
            companyAdminName: this.companyAdminName,
            companyName: this.companyName,
            giftCard: this.giftCard,
        }
    }

    public get customerType(): CustomerType | undefined {
        const { customerType } =
            this.bookingflowStateMachine.getData<BasisState>()
        return customerType
    }

    public get service(): Service | undefined {
        const { serviceType } = this.bookingflowStateMachine.getData()
        return serviceType
    }

    public get basisType(): BasisType | undefined {
        const { basisType } = this.bookingflowStateMachine.getData()
        return basisType
    }
    public get giftCard(): GiftCard | undefined {
        return this._giftCard
    }
    public get address(): Address | undefined {
        return this._address
    }
    public get product(): Product | CompanyLead {
        return this._product
    }
    public get problemDescription(): string | undefined {
        return this._problemDescription
    }
    public get customerPhone(): number | undefined {
        return this._customerPhone
    }
    public get customerName(): string | undefined {
        return this._customerName
    }
    public get acceptConditions(): boolean | undefined {
        return this._acceptConditions
    }
    public get specialNeeds(): boolean | undefined {
        return this._specialNeeds
    }
    public get customerEmail(): string | undefined {
        return this._customerEmail
    }
    public get promoCode(): string | undefined {
        return this._promoCode
    }
    public get klippeCardCode(): string | undefined {
        return this._klippeCardCode
    }
    public get parkingText(): string | undefined {
        return this._parkingText
    }
    public get bookingReasons(): BookingReasons {
        return this._bookingReasons
    }
    public get companyAdminName(): string | undefined {
        return this._companyAdminName
    }
    public get companyName(): string | undefined {
        return this._companyName
    }
    public get voucher(): VoucherOrNone {
        return this.klippeOrGiftCard
    }

    private get customer(): Customer {
        const name = this.customerName || ''
        const email = this.customerEmail || ''
        const phone = this.customerPhone || 12345678
        const companyName = this._companyName || ''
        const companyAdminName = this.companyAdminName || ''
        const customer =
            this._customerType === 'Company'
                ? new Company(phone, companyAdminName, companyName, email)
                : new PrivateCustomer(name, email, phone, true)
        return customer
    }

    private get klippeOrGiftCard(): VoucherOrNone {
        const { giftCard, klippeCardCode } = this.getData()
        if (giftCard) {
            const { code, giftCardProvider, activationCode, providerId } =
                giftCard as GoDreamGiftCard
            if (giftCardProvider === 'RaskRask' && code) {
                return new GiftCard(code, giftCardProvider)
            } else if (
                giftCardProvider === 'GoDream' &&
                code &&
                providerId &&
                activationCode
            ) {
                return new GoDreamGiftCard(code, providerId, activationCode)
            } else if (
                giftCardProvider !== 'RaskRask' &&
                giftCardProvider &&
                code &&
                providerId
            ) {
                return new ThirdPartyGiftCard(
                    code,
                    giftCardProvider,
                    providerId
                )
            } else {
                return {
                    type: 'NONE',
                }
            }
        } else if (klippeCardCode) {
            return new KlippeCard(klippeCardCode)
        } else {
            return {
                type: 'NONE',
            }
        }
    }
}
